import React, { useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
import sanitizeHtml from 'sanitize-html';
import { Typography } from '@mui/material';
import {
  RunningWithErrorsRounded as RunningWithErrorsRoundedIcon,
  AccessTimeRounded as AccessTimeRoundedIcon,
} from '@mui/icons-material';
import apis from '@src/apis';
import { COLOR } from '@src/styles/color';
import { SUB_STATUS } from '@src/constants/voiceCloning';
import { convertFormatDate } from '@src/utils/date';
import { useTranslation } from 'react-i18next';
import route from '@src/constants/route';
import actions from '@src/redux/actions';
import { MOE_PAYWALL_TYPE } from '@src/constants/moengage';
import { validateUserPackageCanUseVoiceCloning } from '@src/services/voiceCloning';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import CrownImg from '@src/assets/images/crown.png';
import { VOICE_PROVIDER } from '@src/constants/voice';
import {
  FEATURE,
  PACK_DURATION,
  PACKAGE_LEVEL,
  PACKAGE_LEVEL_V3,
  PACKAGE_TYPE,
} from '@src/constants/package';
import { checkVietNam } from '@src/utils/checkCountry';
import { useCanUseEOLVoice } from '@src/hooks/useCanUseEOLVoice';
import { checkFeaturePermission } from '@src/services/tts';
import { useMappingPackage } from '@src/hooks/useMappingPackage';
import {
  isVoiceEligibleForTrial,
  isVoiceTrialLimitReached,
} from '@src/services/voiceTrial';
import { trackTryVoice, TRY_VOICE_ACTION } from '@src/services/voiceTracking';
import {
  formatNameForEOL,
  getUpgradePackageLabelKey,
} from '@src/services/voice';
import VoiceCardItem from './VoiceCardItem';
import VoiceListItem from './VoiceListItem';

let currentAudioRef = null;
let currentSetIsPlaying = null;

const VoiceItem = ({
  voice,
  isCard = false,
  ttsUser,
  user,
  usingPackage,
  isTrending,
  isVbee,
  section,
  onChangeVoice,
  onClose,
  isMyVoice,
  isApplyPreset,
  variant,
  isCommunity,
  isDubbing,
  isDialog,
  favouriteVoiceCodes,
  isFavourite,
  onFavoriteToggleRequest,
  onHandleFavoriteProcessingComplete,
  onErrorProcessingFavouriteVoices,
  activeVoiceCode,
  isLeaderboard,
  prizes,
  isOpenFromSpeakerSettings = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();
  const { ttsPreset } = useSelector((state) => state.synthesisRequest);
  const { isPlayingVoicePodium } = useSelector((state) => state.layout);
  const { canUseEOLVoice } = useCanUseEOLVoice();
  const {
    currentPlayingVoiceItemVariant,
    currentPlayingVoiceId,
    currentPlayingVoiceSection,
  } = useSelector((state) => state.audioVoiceLibrary);

  const { getFeatureValue } = useFeatureFlags();
  const userVoiceEolEnabled = getFeatureValue(FEATURE_KEYS.USER_VOICE_EOL, {
    userId: user.id,
  });
  const { handleGetPackageCodeMapping } = useMappingPackage();
  const isPlaying =
    currentPlayingVoiceItemVariant === variant &&
    currentPlayingVoiceId === voice?.id &&
    (!section || currentPlayingVoiceSection === section);

  const audioRef = useRef(new Audio());
  const lastUrlRef = useRef('');

  const clearCurrentPlayingVoice = () => {
    dispatch(actions.audioVoiceLibrary.setCurrentPlayingVoiceItemVariant(null));
    dispatch(actions.audioVoiceLibrary.setPlayingVoiceId(null));
    dispatch(actions.audioVoiceLibrary.setCurrentPlayingVoiceSection(null));
  };

  const setCurrentPlayingVoice = () => {
    dispatch(
      actions.audioVoiceLibrary.setCurrentPlayingVoiceItemVariant(variant),
    );
    dispatch(actions.audioVoiceLibrary.setPlayingVoiceId(voice?.id));
    dispatch(actions.audioVoiceLibrary.setCurrentPlayingVoiceSection(section));
  };

  useEffect(() => {
    const audioEl = audioRef.current;
    audioEl.preload = 'none';
    const onEnded = () => {
      if (currentAudioRef === audioEl) {
        currentAudioRef = null;
        currentSetIsPlaying = null;
      }
      clearCurrentPlayingVoice();
    };
    audioEl.addEventListener('ended', onEnded);
    return () => {
      audioEl.pause();
      audioEl.currentTime = 0;
      audioEl.removeEventListener('ended', onEnded);
      clearCurrentPlayingVoice();
    };
  }, []);

  const handlePlayAudio = (e) => {
    e.stopPropagation();
    const audioEl = audioRef.current;
    const audioUrl = voice?.demo;
    if (!audioEl || isPlaying || !audioUrl) return;

    if (currentAudioRef && currentAudioRef !== audioEl) {
      currentAudioRef.pause();
      currentAudioRef.currentTime = 0;
    }
    currentSetIsPlaying?.(false);

    if (lastUrlRef.current !== audioUrl) {
      audioEl.src = audioUrl;
      audioEl.currentTime = 0;
      lastUrlRef.current = audioUrl;
    }

    currentAudioRef = audioEl;
    audioEl.play();
    setCurrentPlayingVoice();
    trackTryVoice({ voice, action: TRY_VOICE_ACTION.PREVIEW });
  };

  const handlePauseAudio = () => {
    const audioEl = audioRef.current;
    if (audioEl && isPlaying) {
      audioEl.pause();
      if (currentAudioRef === audioEl) {
        currentAudioRef = null;
        currentSetIsPlaying = null;
      }
      clearCurrentPlayingVoice();
    }
  };

  useEffect(() => {
    if (isPlayingVoicePodium) {
      handlePauseAudio();
    }
  }, [isPlayingVoicePodium]);

  const userInFeatureVoiceCloning = getFeatureValue(
    FEATURE_KEYS.VOICE_CLONING,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const { usingDubbingPackage, trialVoiceUsage: stateTrialVoiceUsage } =
    useSelector((state) => state.user);
  const allPackages = useSelector((state) => state.packages.packages);
  const voiceTrialConfig = getFeatureValue(FEATURE_KEYS.TRIAL_VOICE_CONFIG, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
    packageCode: user.packageCode,
  });
  const canUseVoiceCloning = useMemo(
    () =>
      validateUserPackageCanUseVoiceCloning({
        ttsUser,
        voice,
        isUserInWhiteList: userInFeatureVoiceCloning,
      }),
    [ttsUser, voice, userInFeatureVoiceCloning],
  );
  const { defaultGlobalVoice } = useSelector((state) => state.voice);
  const isVietNam = checkVietNam();
  const isDefaultGlobalVoice =
    !isVietNam &&
    usingPackage?.level === PACKAGE_LEVEL.BASIC &&
    voice?.code === defaultGlobalVoice;
  const canUseVoice = () => {
    const isVoiceCloning =
      voice?.provider === VOICE_PROVIDER.VBEE_VOICE_CLONING &&
      voice?.userId === ttsUser?.id &&
      voice?.status === SUB_STATUS.PRIVATE;

    if (isVoiceCloning && !canUseVoiceCloning) return false;

    if (!voice?.features?.length) return true;

    if (!usingDubbingPackage && isDefaultGlobalVoice) return true;

    const eolVoice = voice?.eolDate;
    if (eolVoice && canUseEOLVoice({ voice, user: ttsUser })) return true;

    return voice.features.every((item) =>
      checkFeaturePermission(ttsUser?.features, item),
    );
  };

  const getRetentionDaysLabel = () => {
    const isPublicVoiceCloning = voice?.status === SUB_STATUS.PUBLIC;

    if (isPublicVoiceCloning) {
      const publicVoiceCloningTagData = {};
      const isVoiceCloningAboutToRemoved =
        isPublicVoiceCloning && voice?.discardAt
          ? moment().isBefore(voice?.discardAt)
          : false;

      const daysLeftBeforeVoiceCloningDiscard =
        isPublicVoiceCloning && moment(voice?.discardAt).diff(moment(), 'days');

      const voiceCloningRetentionDays = isPublicVoiceCloning
        ? voice?.retentionDays
        : null;

      if (isVoiceCloningAboutToRemoved) {
        publicVoiceCloningTagData.icon = (
          <RunningWithErrorsRoundedIcon
            sx={{ color: COLOR.redPrimary, fontSize: 16 }}
          />
        );
        publicVoiceCloningTagData.title = t(
          daysLeftBeforeVoiceCloningDiscard ? 'dayNumber' : 'today',
          {
            days: daysLeftBeforeVoiceCloningDiscard,
          },
        );
        publicVoiceCloningTagData.titlePopover = t(
          daysLeftBeforeVoiceCloningDiscard ? 'dayNumber' : 'today',
          {
            days: daysLeftBeforeVoiceCloningDiscard,
          },
        );
        publicVoiceCloningTagData.textColor = COLOR.redPrimary;
        publicVoiceCloningTagData.bgColor = COLOR.bgRedPrimary;
        publicVoiceCloningTagData.isVoiceAboutToBeRemoved = true;
        publicVoiceCloningTagData.contentTooltip = (
          <Typography sx={{ fontSize: '14px' }}>
            {t(
              isMyVoice ? 'myVoiceCloningDiscardAt' : 'voiceCloningDiscardAt',
              {
                date: convertFormatDate(voice?.discardAt),
              },
            )}
          </Typography>
        );

        return publicVoiceCloningTagData;
      }

      const keyRetentionDays =
        voiceCloningRetentionDays === 365 ? 'yearNumber' : 'dayNumber';

      publicVoiceCloningTagData.icon = (
        <AccessTimeRoundedIcon sx={{ color: COLOR.indigo[64], fontSize: 16 }} />
      );
      publicVoiceCloningTagData.title = t(keyRetentionDays, {
        days: voiceCloningRetentionDays,
        value: 1,
      });
      publicVoiceCloningTagData.titlePopover = t('daysRetentionAfterRemove', {
        value: t(keyRetentionDays, {
          days: voiceCloningRetentionDays,
          value: 1,
        }),
      });
      publicVoiceCloningTagData.bgColor = COLOR.skyBlue;
      publicVoiceCloningTagData.contentTooltip = (
        <Typography sx={{ fontSize: '14px' }}>
          {t('voiceCloningRetentionDaysV2', {
            value: t(keyRetentionDays, {
              days: voiceCloningRetentionDays,
              value: 1,
            }),
          })}
        </Typography>
      );

      return publicVoiceCloningTagData;
    }

    const isAboutToBeRemoved = voice?.eolDate
      ? moment().isBefore(voice?.eolDate)
      : false;
    const daysLeftBeforeVoiceInactive = isAboutToBeRemoved
      ? moment(voice?.eolDate).diff(moment(), 'days')
      : null;
    const eolTagNameData = {
      textColor: COLOR.redPrimary,
      isVoiceAboutToBeRemoved: isAboutToBeRemoved,
      title: isAboutToBeRemoved
        ? t(daysLeftBeforeVoiceInactive ? 'dayNumber' : 'today', {
            days: daysLeftBeforeVoiceInactive,
          })
        : undefined,
      titlePopover: isAboutToBeRemoved
        ? t(daysLeftBeforeVoiceInactive ? 'dayNumber' : 'today', {
            days: daysLeftBeforeVoiceInactive,
          })
        : undefined,
      icon: (
        <RunningWithErrorsRoundedIcon
          sx={{ color: COLOR.redPrimary, fontSize: 16 }}
        />
      ),
      contentTooltip: (
        <Typography
          sx={{ fontSize: '14px' }}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(
              t(
                userVoiceEolEnabled
                  ? 'discontinuedAfterTimeV2'
                  : 'discontinuedAfterTime',
                {
                  date: moment(voice?.eolDate)?.format('DD/MM/YYYY'),
                  name: formatNameForEOL(voice?.name, userVoiceEolEnabled),
                },
              ),
              {
                allowedTags: ['b', 'span'],
                allowedAttributes: {
                  '*': ['style'],
                },
              },
            ),
          }}
        />
      ),
    };

    if (isAboutToBeRemoved) {
      return eolTagNameData;
    }
    return null;
  };

  const handleLimitedFeatureVoiceDialog = () => {
    const isBasicGlobalVoice = voice?.features?.includes(
      FEATURE.BASIC_GLOBAL_VOICE,
    );
    const isAdvancedGlobalVoice = voice?.features?.includes(
      FEATURE.ADVANCED_GLOBAL_VOICE,
    );
    if (isOpenFromSpeakerSettings) {
      let packageName = t('standard');
      if (isBasicGlobalVoice) {
        packageName = t('enterprise');
      }
      if (isAdvancedGlobalVoice) {
        packageName = t('vip');
      }
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('voiceUpgradeSrt', { packageName }),
          img: CrownImg,
          classNameImg: 'custom-img',
          maxWidth: 'sm',
          onUpgrade: () => {
            history.push(route.PAYMENT);
            dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
          },
        }),
      );
      return;
    }
    if (isAdvancedGlobalVoice) {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('voiceUpgradeVip'),
          img: CrownImg,
          classNameImg: 'custom-img',
          maxWidth: 'sm',
          onUpgrade: () => {
            const upgradePackage = handleGetPackageCodeMapping({
              level: PACKAGE_LEVEL_V3.VIP,
              type: PACKAGE_TYPE.STUDIO,
              expiresIn: PACK_DURATION.YEAR,
            });
            history.push(`${route.PAYMENT}?package=${upgradePackage}`);
            dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
          },
        }),
      );

      return;
    }

    if (isBasicGlobalVoice) {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('voiceUpgradeSpecialOrVip'),
          img: CrownImg,
          classNameImg: 'custom-img',
          maxWidth: 'sm',
          onUpgrade: () => {
            history.push(route.PAYMENT);
            dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
          },
        }),
      );
      return;
    }

    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: t('limitedFeatureDesc', { feature: t('useThisVoice') }),
      }),
    );
  };

  const handleOpenVoiceTrialLimitDialog = (limitedVoice = voice) => {
    const packageName = t(
      getUpgradePackageLabelKey({
        voices: [limitedVoice],
        userFeatures: ttsUser?.features,
        packages: allPackages,
      }),
    );
    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: t('trialVoiceLimitDescription', {
          voiceName: limitedVoice?.name || '',
          packageName,
        }),
        img: CrownImg,
        classNameImg: 'custom-img',
        maxWidth: '640px',
        paywall: {
          type: MOE_PAYWALL_TYPE.VOICE_TRIAL_LIMIT,
          voiceCode: limitedVoice?.code,
        },
        onUpgrade: () => {
          const upgradePackage = handleGetPackageCodeMapping({
            level: PACKAGE_LEVEL_V3.VIP,
            type: PACKAGE_TYPE.STUDIO,
            expiresIn: PACK_DURATION.YEAR,
          });
          history.push(`${route.PAYMENT}?package=${upgradePackage}`);
          dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
        },
      }),
    );
  };
  const handleUseVoice = (e) => {
    e.stopPropagation();
    const canUseCurrentVoice = canUseVoice();
    trackTryVoice({ voice, action: TRY_VOICE_ACTION.USE });
    const isTrialEligible =
      isVoiceEligibleForTrial(voice, voiceTrialConfig) &&
      !isOpenFromSpeakerSettings;
    const isUsingVoiceTrial = isTrialEligible && !canUseCurrentVoice;

    if (
      isUsingVoiceTrial &&
      isVoiceTrialLimitReached(voice, voiceTrialConfig, stateTrialVoiceUsage)
    ) {
      handleOpenVoiceTrialLimitDialog(voice);
      return;
    }

    if (!canUseCurrentVoice && !isTrialEligible) {
      handleLimitedFeatureVoiceDialog();
      return;
    }

    if ([route.VOICE_LIBRARY, route.VOICE_LEADERBOARD].includes(pathname)) {
      dispatch(actions.synthesisRequest.updateVoice(voice));
      history.push(route.TTS);
    }

    if (onChangeVoice && onClose) {
      onChangeVoice(voice);
      if (ttsPreset && ttsPreset?.voice?.id !== voice.id && !isApplyPreset) {
        dispatch(actions.synthesisRequest.updateCurrentTTSPreset(null));
      }
      onClose();
    }
  };

  const handleUpdateFavouriteVoices = async ({ voiceCode, action }) => {
    const data = await apis.favouriteVoices.updateFavouriteVoices({
      voiceCode,
      action,
    });
    if (data?.status) {
      onHandleFavoriteProcessingComplete({ voice });
    } else {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'errorOccurred',
        }),
      );
      onErrorProcessingFavouriteVoices(voiceCode);
      throw new Error();
    }
  };

  return (
    <>
      {/* //Todo: Voice list item */}
      {isCard ? (
        <VoiceCardItem
          voice={voice}
          retentionDaysLabel={getRetentionDaysLabel()}
          onUseVoice={handleUseVoice}
          isPlaying={isPlaying}
          onPlayAudio={handlePlayAudio}
          onPauseAudio={handlePauseAudio}
          user={user}
          onUpdateFavouriteVoices={handleUpdateFavouriteVoices}
          isDialog={isDialog}
          favouriteVoiceCodes={favouriteVoiceCodes}
          onFavoriteToggleRequest={onFavoriteToggleRequest}
          activeVoiceCode={activeVoiceCode}
          prizes={prizes}
        />
      ) : null}
      {!isCard ? (
        <VoiceListItem
          voice={voice}
          retentionDaysLabel={getRetentionDaysLabel()}
          onUseVoice={handleUseVoice}
          isTrending={isTrending}
          isVbee={isVbee}
          section={section}
          isMyVoice={isMyVoice}
          isPlaying={isPlaying}
          onPlayAudio={handlePlayAudio}
          onPauseAudio={handlePauseAudio}
          user={user}
          isCommunity={isCommunity}
          isDubbing={isDubbing}
          onUpdateFavouriteVoices={handleUpdateFavouriteVoices}
          isDialog={isDialog}
          favouriteVoiceCodes={favouriteVoiceCodes}
          isFavourite={isFavourite}
          onFavoriteToggleRequest={onFavoriteToggleRequest}
          activeVoiceCode={activeVoiceCode}
          isLeaderboard={isLeaderboard}
          prizes={prizes}
        />
      ) : null}
    </>
  );
};

export default VoiceItem;
